(() => {
  'use strict';
  if (window.MomoCommerce) return;
  const config = window.MomoCommerceConfig || {};
  const root = config.root || '/';
  const cartUrl = config.cartUrl || `${root}cart`;
  const companionExcluded = new Set(['perfect-form', 'mass-muscle-vault']);
  const pending = new Set();
  let checkoutPending = false;
  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, {detail}));
  const request = async (path, options) => {
    const response = await fetch(path, {credentials: 'same-origin', ...options});
    const body = await response.json();
    if (!response.ok || body.status || body.errors) {
      throw new Error(typeof body.description === 'string' ? body.description : 'your bag could not be updated. please try again.');
    }
    return body;
  };
  const readCart = async () => {
    const cart = await request(`${root}cart.js`, {cache: 'no-store'});
    if (!Array.isArray(cart.items)) throw new Error('your bag could not be checked. please try again.');
    return cart;
  };
  const post = (path, body) => request(path, {
    method: 'POST', headers: {'Content-Type': 'application/json', 'Accept': 'application/json'}, body: JSON.stringify(body)
  });
  const confirmedAdd = (response, context = {}) => {
    const items = Array.isArray(response.items) ? response.items : [response];
    items.forEach(item => {
      const variantId = item.variant_id || item.id;
      if (!variantId) return;
      emit('momo:cart-added', {
        trigger: context.trigger || 'product_form', product_handle: item.handle || '',
        product_title: item.product_title || item.title || '', variant_id: String(variantId),
        quantity: Number(context.quantity || item.quantity || 1),
        product_price: Number(item.final_price ?? item.price ?? 0) / 100,
        currency: config.currency || window.Shopify?.currency?.active || ''
      });
    });
  };
  const cartError = (error, context = {}) => emit('momo:cart-error', {
    trigger: context.trigger || 'product_form', variant_id: String(context.variantId || ''),
    message: error?.message || 'your bag could not be updated. please try again.'
  });
  const blockedCompanion = cart => cart.items.some(item => companionExcluded.has(item.handle));
  const syncCartCount = cart => {
    if (!Number.isInteger(cart.item_count)) return;
    document.querySelectorAll('[data-momo-cart-count]').forEach(node => {node.textContent = `[${cart.item_count}]`;});
    document.querySelectorAll('[data-momo-cart-link]').forEach(node => {node.setAttribute('aria-label', `bag, ${cart.item_count} items`);});
  };
  const setStatus = (container, message) => {
    let status = container.querySelector('[data-momo-cart-status]');
    if (!status) {
      status = document.createElement('p');
      status.dataset.momoCartStatus = '';
      status.className = 'momo-cart-status';
      status.setAttribute('role', 'alert');
      container.appendChild(status);
    }
    status.textContent = message;
    return status;
  };
  const classifyAppLink = href => {
    let url;
    try { url = new URL(href, window.location.origin); } catch { return null; }
    if (!['https:', 'http:'].includes(url.protocol)) return null;
    if (['app.momomuscle.com', 'momomuscle.app', 'www.momomuscle.app'].includes(url.hostname)) return 'app_explore';
    if (url.hostname === 'playbook.app.link') return url.pathname === '/h1SLbZU476b' ? 'member_open' : 'app_open';
    if (['my.playbookapp.io', 'playbookapp.io'].includes(url.hostname)) {
      if (/\/checkout\/?$/.test(url.pathname)) return 'app_checkout';
      if (/\/(login|sign-in)\/?$/.test(url.pathname)) return 'member_open';
      return 'app_explore';
    }
    return null;
  };
  // These are explicit native forms owned by this theme. App and product-form handlers remain separate.
  const addForm = async form => {
    if (pending.has(form)) return;
    pending.add(form);
    const button = form.querySelector('[type="submit"]');
    const container = form.closest('[data-momo-companion]') || form;
    const companion = form.hasAttribute('data-momo-companion-form');
    const data = new FormData(form);
    const context = {trigger: form.dataset.momoTrigger || (companion ? 'perfect_form_companion' : 'native_form'), variantId: data.get('id'), quantity: Number(data.get('quantity') || 1)};
    if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
    setStatus(container, '');
    try {
      if (companion) {
        const cart = await readCart();
        if (blockedCompanion(cart)) {
          setStatus(container, 'perfect form is already in your bag or included in your vault.');
          return;
        }
      }
      const added = await request(`${root}cart/add.js`, {method: 'POST', headers: {'Accept': 'application/json'}, body: data});
      if (!added.id && !added.items?.length) throw new Error('your bag could not be confirmed. please open your bag before trying again.');
      confirmedAdd(added, context);
      window.location.assign(cartUrl);
    } catch (error) {
      setStatus(container, error.message || 'your bag could not be updated. please try again.');
      cartError(error, context);
    } finally {
      pending.delete(form);
      if (button) { button.disabled = false; button.removeAttribute('aria-busy'); }
    }
  };
  const checkoutSingle = async (variantId, button) => {
    if (checkoutPending) return;
    checkoutPending = true;
    const container = button?.parentElement || document.body;
    const context = {trigger: 'protocol_cta', variantId, quantity: 1};
    if (button) { button.setAttribute('aria-disabled', 'true'); button.setAttribute('aria-busy', 'true'); }
    setStatus(container, '');
    try {
      if (!Number(variantId)) throw new Error('this download is unavailable. please try again later.');
      let cart = await readCart();
      const line = cart.items.find(item => String(item.variant_id) === String(variantId));
      if (line && line.quantity !== 1) {
        cart = await post(`${root}cart/update.js`, {updates: {[variantId]: 1}});
      } else if (!line) {
        const added = await post(`${root}cart/add.js`, {items: [{id: Number(variantId), quantity: 1}]});
        if (!added.id && !added.items?.length) throw new Error('your bag could not be confirmed. please open your bag before trying again.');
        confirmedAdd(added, context);
        cart = await readCart();
      }
      if (!cart.items?.some(item => String(item.variant_id) === String(variantId) && item.quantity === 1)) {
        throw new Error('your bag could not be confirmed. please open your bag before trying again.');
      }
      window.location.assign(`${root}checkout`);
    } catch (error) {
      setStatus(container, error.message || 'your bag could not be updated. please try again.');
      cartError(error, context);
    } finally {
      checkoutPending = false;
      if (button) { button.removeAttribute('aria-disabled'); button.removeAttribute('aria-busy'); }
    }
  };
  window.MomoCommerce = {confirmedAdd, cartError, addForm, checkoutSingle, classifyAppLink, syncCartCount};
  document.addEventListener('submit', event => {
    const form = event.target;
    if (event.defaultPrevented || !form.matches?.('form[data-momo-add]') || form.closest('product-form')) return;
    event.preventDefault();
    addForm(form);
  });
})();

// Keep incoming campaign attribution on the store-to-app handoff. No storage or beacon.
(() => {
  'use strict';
  const incoming = new URLSearchParams(window.location.search);
  const tags = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id', 'fbclid', 'campaign_id', 'adset_id', 'ad_id']
    .map(key => [key, incoming.get(key)])
    .filter(([key, value]) => value && value.length <= (key === 'fbclid' ? 2048 : 200));
  if (!tags.length) return;
  const stores = new Set(['momomuscle.com', 'www.momomuscle.com', 'babe-go-lift.myshopify.com']);
  const decorate = link => {
    const href = link.getAttribute('href');
    if (!href || href.startsWith('#') || link.hasAttribute('download')) return;
    let destination;
    try { destination = new URL(href, document.baseURI); } catch { return; }
    if (destination.protocol !== 'https:' || destination.username || destination.password) return;
    const store = stores.has(destination.hostname);
    const app = destination.hostname === 'app.momomuscle.com';
    const checkout = destination.hostname === 'my.playbookapp.io' && /^\/morgan-olson\/checkout\/?$/.test(destination.pathname);
    if (!store && !app && !checkout) return;
    // Member/account and native commerce endpoints are not acquisition destinations.
    if (store && /^\/(?:account|cart|checkout|checkouts|apps)(?:\/|$)/.test(destination.pathname)) return;
    tags.forEach(([key, value]) => {
      if (!destination.searchParams.has(key)) destination.searchParams.set(key, value);
    });
    link.href = destination.href;
  };
  const decorateAll = () => document.querySelectorAll('a[href]').forEach(decorate);
  decorateAll();
  document.addEventListener('shopify:section:load', decorateAll);
  // Delegation also covers links inserted after load and modified/middle clicks.
  ['click', 'auxclick'].forEach(type => document.addEventListener(type, event => {
    const link = event.target.closest?.('a[href]');
    if (link) decorate(link);
  }, true));
})();
